<template>
  <div>
    <v-alert type="info">

      <template #prepend>
        <v-icon>
          {{ icons.mdiInformationOutline }}
        </v-icon>
      </template>

      <template>
        <div class="pl-3">
          {{ $t('Kaikki muutokset kirjataan lokiin') }}
        </div>
      </template>

      <template #append>
        <v-btn icon @click="viewLog">
          <v-icon>
            {{ icons.mdiEye }}
          </v-icon>

        </v-btn>
      </template>
    </v-alert>
    <v-select class="mb-4" outlined :items="allAdmins" ref="addUserSelect" return-object :item-text="'name'"
      :item-value="'id'" :label="$t('Lisää uusi palautteiden käsittelijä')" @change="userAdded"
      :hint="$t('Palautteiden käsittelijäksi voidaan...')" :persistent-hint="true">
    </v-select>
    <div style="position: relative;">
      <v-simple-table>

        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left" style="min-width: 240px;">
                {{ $t('Nimi') }}
              </th>
              <th>
                {{ $t('Valitse käsittelyoikeudet') }}
              </th>
              <th class="text-left">
                {{ $t('Ilmoitus sähköpostiin') }}
              </th>
              <th>
                {{ $t('Push-viesti puhelimeen') }}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="!isLoading" :style="{ 'opacity': isLoading ? 0 : 1 }">
            <tr v-for="manager in managers" :key="manager.id">
              <td>
                <v-avatar size="24">
                  <v-img v-if="manager.picture" :src="manager.picture"></v-img>
                  <v-icon v-else>{{ icons.mdiAccountOutline }}</v-icon>
                </v-avatar>
                &nbsp;
                {{ manager.name }}
              </td>
              <td>
                <v-select class="mt-5" outlined :label="$t('Käsittelyoikeudet')" item-value="id" item-text="title"
                  :items="categories" v-model="manager.category_permissions" multiple></v-select>
              </td>
              <td>
                <v-checkbox v-model="manager.send_email"></v-checkbox>
              </td>
              <td>
                <v-checkbox v-model="manager.send_push"></v-checkbox>
              </td>
              <td>
                <v-btn color="error" class="mr-2 my-1" x-small dark @click="removeManager(manager.user)">
                  {{ $t('Poista') }}
                </v-btn>
                <v-btn color="primary" class="my-1" x-small dark @click="updateManager(manager)">
                  {{ $t('Tallenna') }}
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tbody v-if="isLoading">
            <tr>
              <td colspan="4" style="text-align: center">
                <br><br>
                <v-progress-circular indeterminate>
                </v-progress-circular>
                <br><br>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <div class="pa-2">
        <br>
        <v-alert text>

          <p>Sähköposti-ilmoitusten lähetykseen käytetään SendGrid-palvelua <a target="_blank"
              href="https://sendgrid.com/">https://sendgrid.com/</a> </p>
          <p>Palvelun tietosuojaseloste <a target="_blank"
              href="https://www.twilio.com/legal/privacy">https://www.twilio.com/legal/privacy</a>
          </p>
          <p>
            Palautteet lähetetään osoitteesta noreply@piirikit.fi. Mikäli et saa ilmoituksia palautteista sähköpostiisi,
            tarkista roskapostikansio ja lisää tarvittaessa osoite sähköpostiohjelman yhteystietoihin tai luotetuksi
            lähettäjäksi.
          </p>
          <p>
            Huomioithan, että palautteita ei voi lukea suoraan puhelimen sovelluksesta. Puhelimeen tuleva push-viesti
            kertoo ainoastaan
            uuden palautteen saapumisesta.
          </p>
        </v-alert>
      </div>
    </div>
    <v-snackbar v-model="snackbar" color="success">

      <template #default>
        <v-icon>{{ icons.mdiCheckCircleOutline }}</v-icon>
        {{ $t('Tallennus onnistui') }}
      </template>
    </v-snackbar>
    <v-dialog v-model="logModal" max-width="920px" scrollable>
      <v-card>
        <v-card-title>{{ $t('Loki') }}</v-card-title>
        <v-card-text>
          <v-simple-table>
            <thead>
              <tr>
                <th>{{ $t('Aikaleima') }}</th>
                <th>{{ $t('Suorittanut') }}</th>
                <th>{{ $t('Kohdekäyttäjä') }}</th>
                <th>{{ $t('Tapahtuma') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs">
                <td>
                  {{ getDateTime(log.timestamp) }}
                </td>
                <td>
                  {{ log.user }}
                </td>
                <td>
                  {{ log.target_user }}
                </td>
                <td>
                  <v-icon size="16" v-if="log.log_type == 2" color="error">
                    {{ icons.mdiMinusCircle }}
                  </v-icon>
                  <span :style="{ color: log.log_type == 2 ? 'red' : 'blue' }">
                    {{ log.description }}
                  </span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import useAuth from "@/useAuth";
import { computed, onMounted, ref } from "@vue/composition-api";
import { mdiEye, mdiPlusCircle, mdiMinusCircle, mdiDeleteOutline, mdiAccountOutline, mdiCheckCircleOutline, mdiInformationOutline, mdiFloppy } from "@mdi/js";
import adminApi from "@/api/admin";
import { getDateTime } from "@core/utils/datetime_helpers";

export default {
  setup() {
    const { tenant } = useAuth();
    const isLoading = ref(false);
    const saved = ref(false);
    const managers = ref([]);
    const _allAdmins = ref([]);
    const selectedAdmin = ref(null);
    const addUserSelect = ref(null);
    const categories = ref([]);
    const snackbar = ref(false);
    const logModal = ref(false);
    const logs = ref([]);

    const init = async () => {
      await fetchCategories();
      await fetchAllAdmins();
      await fetchManagers();
    }

    async function fetchCategories() {
      const res = await adminApi.getFeedbackCategories();
      categories.value = res.data;
    }

    const fetchAllAdmins = async () => {
      const res = await adminApi.getAllAdmins();
      _allAdmins.value = res.data.results;
    }

    const allAdmins = computed(() => {
      return _allAdmins.value.map(p => {
        return {
          id: p.id,
          name: p.last_name + " " + p.first_name
        }
      })
    })

    const fetchManagers = async () => {
      isLoading.value = true;
      const res = await adminApi.getFeedbackManagers();
      managers.value = res.data;
      isLoading.value = false;
    };


    onMounted(() => {
      init();
    });

    const userAdded = async (e) => {

      if (e.id === 0) {
        addUserSelect.value.reset();
        return;
      }

      const selectedUser = e;

      if (managers.value.some(a => a.user === selectedUser.id))
        return;

      const manager = {
        user: selectedUser.id,
        name: selectedUser.name,
        send_email: true,
        send_push: true
      }

      const res = await adminApi.addFeedbackManager({
        user: manager.user,
        send_email: manager.send_email,
        send_push: manager.send_push
      });

      if (res.ok) {
        managers.value.push(manager);
      }

      addUserSelect.value.reset();
    }

    const updateManager = async (manager) => {
      snackbar.value = false;
      const res = await adminApi.updateFeedbackManager(manager);
      if (!res.ok) {
        await fetchManagers();
      }
      else {
        snackbar.value = true;
      }
    }

    const removeManager = async (managerId) => {
      if (confirm("Haluatko varmasti poistaa käyttäjän oikeudet käsitellä palautteita?")) {
        const res = await adminApi.removeFeedbackManager(managerId);
        if (res.ok) {
          await fetchManagers();
        }
      }
    }

    const viewLog = async () => {
      await fetchLog();
      logModal.value = true;
    }

    const fetchLog = async () => {
      const res = await adminApi.getFeedbackManagerLog();
      logs.value = res.data;
    }

    // const save = async () => {
    //   await adminApi.saveFeedbackManagers(managers.value.map(a => a.id));
    // }

    return {
      tenant,
      allAdmins,
      selectedAdmin,
      isLoading,
      categories,
      userAdded,
      removeManager,
      logModal,
      updateManager,
      saved,
      addUserSelect,
      managers,
      getDateTime,
      logs,
      viewLog,
      snackbar,
      icons: {
        mdiDeleteOutline,
        mdiAccountOutline,
        mdiInformationOutline,
        mdiPlusCircle,
        mdiMinusCircle,
        mdiCheckCircleOutline,
        mdiEye,
        mdiFloppy,
      }
    };
  },
};
</script>
